import { LatLngBounds, LatLngExpression, Map, point } from 'leaflet';
import { RefObject, useEffect, useRef } from 'react';
import useMapConfig from '../hooks/useMapConfig';

export interface MapManagerProps {
  center?: LatLngExpression;
  zoom?: number;
  mapRef: RefObject<Map>;
  bounds?: LatLngBounds;
  padding?: boolean;
  animate?: boolean;
  renderKey?: string;
  afterInitCallback?: (map: Map) => void;
}

export const MapManager = ({
  mapRef,
  bounds,
  padding = true,
  center,
  zoom,
  animate = false,
  renderKey,
  afterInitCallback,
}: MapManagerProps): null => {
  // Create a ref to be able to track and change the intitialisation variable without causing a re-render.
  const initialisationRef = useRef<boolean>(false);

  const { maxNativeZoom } = useMapConfig();

  // Note: Include renderKey as a dependency to force the effect to run when the count is incremented, even if no other props change.
  useEffect(() => {
    const map = mapRef.current;
    const hasBeenInitialised = initialisationRef.current;

    if (map && (bounds || center)) {
      /*
      Fixme: The bounds can sometimes be equivalent to a latlng value of [NaN, NaN].
      The only current method for reproducing the error is to go to a page with the LocationFilter, 
      then switch to a different browser tab and modify the page's code in the IDE.
      When navigating back to the browser tab, the error will now display.
      I believe this is an issue caused by a combination of hot reloading and how leaflet renders outside of the react lifecycle.
      The temporary fix is to catch the error and log it to the console, rather than letting it crash the app.
      */
      try {
        if (bounds) {
          // Predict the best zoom level to fit the bounds in the map viewport (with settings).
          const maxZoom = map.getBoundsZoom(bounds, false, padding ? point([40, 40]) : undefined);
          const zoomLevel = Math.min(maxNativeZoom, maxZoom);
          map.setView(bounds.getCenter(), zoomLevel, {
            animate: hasBeenInitialised,
          });
        } else if (center) {
          // Make sure the zoom is never greater than the maxNativeZoom.
          const zoomLevel =
            typeof zoom === 'number' ? Math.min(maxNativeZoom, zoom) : maxNativeZoom;
          map.setView(center, zoomLevel, { animate: hasBeenInitialised });
        }
      } catch (error) {
        console.debug('MapManager:', error);
      }

      // Update the initialisationRef to true after the first setView call.
      if (!hasBeenInitialised) {
        initialisationRef.current = true;

        // Execute this callback only once, after the map has been initialised and the map view set.
        // The map view is set instantaneously on the first render using setView.
        afterInitCallback?.(map);
      }
    }
  }, [mapRef, bounds, padding, center, zoom, animate, renderKey, maxNativeZoom, afterInitCallback]);

  return null;
};

export default MapManager;
